import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Layout from '../component/layout';
import { toastify } from '../../../utils/toast';
import ConfirmDialog from '../component/confirmDialog';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { closeTicketURL, finishReviewURL, getCustomersURL, getTicketURL, getTicketLandlordURL } from '../../../utils/urls';

const StartReview = () => {
    const navigate = useNavigate();
    const { ticketId } = useParams();
    const [currentStep, setCurrentStep] = useState('StartReview');
    const [needsFix, setNeedsFix] = useState('');
    const [closeReason, setCloseReason] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [reviewText, setReviewText] = useState('');
    const [costAttachment, setCostAttachement] = useState(null);
    const [payer, setPayer] = useState('');
    const [payerDetails, setPayerDetails] = useState(null);
    const [payerType, setPayerType] = useState('');
    const [completedSteps, setCompletedSteps] = useState(['StartReview']);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [allCustomers, setAllCustomers] = useState([])
    const [payerSearchTerm, setPayerSearchTerm] = useState('');
    const location = useLocation();
    const { requester, description } = location.state || {};
    const [fullTicketData, setFullTicketData] = useState(null);
    const steps = ['StartReview', 'UploadReview', 'EstimatedCosts', 'PayerApproval'];
    // const [services, setServices] = useState([
    //     { description: '', amount: '' },
    // ]);
    const [services, setServices] = useState([
        { description: '', quantity: 1, unitPrice: 0, amount: 0 }
    ]);
    const [totalCost, setTotalCost] = useState(0);
    const navigationTicket = location.state?.ticket;
    const currentTicket = fullTicketData || navigationTicket;

    const getCompleteTicketData = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getTicketURL}/${facilityId}/${ticketId}`, 'GET', {});
            if (response.success) {
                const fetchedTicket = response.data.ticket;
                const ticketWithRequesterInfo = {
                    ...fetchedTicket,
                    fullName: fetchedTicket.RequesterInfo?.fullName || 'Unknown',
                    phoneNumber: fetchedTicket.RequesterInfo?.phoneNumber || '',
                    email: fetchedTicket.RequesterInfo?.email || '',
                    requester: fetchedTicket.RequesterInfo?.requester || '',
                };
                setFullTicketData(ticketWithRequesterInfo);
                return ticketWithRequesterInfo;
            } else {
                throw new Error(response.message);
            }
        } catch (err) {
            toastify(err.message, 'error');
            return null;
        }
    };
    // Handle field changes dynamically
    const handleServiceChange = (index, field, value) => {
        const updatedServices = [...services];

        // Update the field that changed
        updatedServices[index] = {
            ...updatedServices[index],
            [field]: value
        };

        // Auto-calculate amount when quantity or unitPrice changes
        if (field === 'quantity' || field === 'unitPrice') {
            const quantity = parseInt(updatedServices[index].quantity) || 1;
            const unitPrice = parseFloat(updatedServices[index].unitPrice) || 0;
            updatedServices[index].amount = quantity * unitPrice;
        }

        setServices(updatedServices);
        calculateTotalCost(updatedServices);
    };
    // Add a new service field
    const addServiceField = () => {
        setServices([...services, { description: '', quantity: 1, unitPrice: '', amount: 0 }]);
    };
    // Remove a specific service field
    const removeServiceField = (index) => {
        const updatedServices = services.filter((_, i) => i !== index);
        setServices(updatedServices);
        calculateTotalCost(updatedServices);
    };
    // Calculate the total cost
    const calculateTotalCost = (servicesArray) => {
        const total = servicesArray.reduce(
            (sum, service) => {
                // Calculate amount based on quantity and unitPrice
                const quantity = parseInt(service.quantity) || 1;
                const unitPrice = parseFloat(service.unitPrice) || 0;
                const amount = quantity * unitPrice;

                return sum + amount;
            },
            0
        );
        setTotalCost(total);
    };

    const hideConfirmDialog = () => {
        setConfirmVisible(false);
        setIsClosing(false);
    };

    const getCurrentUser = async () => {
        try {
            const currentUserValue = await getItem("APPUSER");
            let currentUser;
            // Check if the value needs to be parsed
            if (typeof currentUserValue === "string") {
                try {
                    currentUser = JSON.parse(currentUserValue);
                } catch (err) {
                    console.error("Error parsing APPUSER:", err);
                }
            } else {
                currentUser = currentUserValue;
            }

            if (currentUser?.user) {
                return currentUser.user.phoneNumber;
            } else {
                console.error("Invalid user structure:", currentUser);
                return null;
            }
        } catch (err) {
            console.error(err.message);
            return null;
        }
    };

    const fetchCustomersForFacility = async () => {
        try {
            const value = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getCustomersURL}/${value}`, 'GET', {});

            if (response.success) {
                setAllCustomers(response.data);
            } else {
                throw new Error('Failed to fetch customers');
            }
        } catch (error) {
            console.error(error.message);
            toastify(error.message, 'error');
        }
    };

    const handleCloseTicket = async () => {
        try {
            if (!closeReason) {
                toastify("Please provide a reason for closing the ticket.", "error");
                return;
            }

            setIsClosing(true); // Start loading

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(closeTicketURL + '/' + ticketId + '/' + facilityId, 'POST', {
                status: 'closed',
                reason: closeReason,
            });

            if (response.success) {
                let successMessage = 'Ticket successfully closed and moved to Closed.';

                // Add notification info if available
                if (response.warning) {
                    successMessage += ` Note: ${response.warning}`;
                }

                toastify(successMessage, 'success');
                setConfirmVisible(false);
                navigate('/facility/ticket_management/tickets');
            } else {
                throw new Error(response.message);
            }
        } catch (err) {
            toastify(err.message, 'error');
        } finally {
            setIsClosing(false); // Always stop loading
        }
    };

    const validateStartReview = () => {
        if (!needsFix) {
            toastify("Please select if the issue needs to be fixed.", "error");
            return false;
        }
        if (needsFix === "no") {
            if (!closeReason.trim()) {
                toastify("Please provide a reason for closing the ticket.", "error");
                return false;
            }
            return true;
        }
        return true;
    };

    const validateUploadReview = () => {
        if (images.length > 5) {
            toastify("You can upload up to 5 images only.", "error");
            return false;
        }
        if (!reviewText.trim()) {
            toastify("Review text is required.", "error");
            return false;
        }
        return true;
    };

    const validateEstimatedCosts = () => {
        if (services.length === 0) {
            toastify("Please add at least one service.", "error");
            return false;
        }
        for (let i = 0; i < services.length; i++) {
            const service = services[i];
            if (!service.description?.trim()) {
                toastify(`Service ${i + 1}: Description is required.`, "error");
                return false;
            }
            if (!service.amount || isNaN(service.amount) || parseFloat(service.amount) <= 0) {
                toastify(`Service ${i + 1}: Amount must be a positive number.`, "error");
                return false;
            }
        }
        return true;
    };

    const handleNextStep = () => {
        if (currentStep === "StartReview" && !validateStartReview()) return;
        if (currentStep === "UploadReview" && !validateUploadReview()) return;
        if (currentStep === "EstimatedCosts" && !validateEstimatedCosts()) return;

        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex < steps.length - 1) {
            const nextStep = steps[currentIndex + 1];
            setCompletedSteps([...completedSteps, nextStep]);
            setCurrentStep(nextStep);
        }
    };

    const handleBackToPrevious = () => {
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex > 0) {
            setCurrentStep(steps[currentIndex - 1]);
        }
    };

    const handleImageChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const maxFiles = 5;
        if (images.length + newFiles.length > maxFiles) {
            toastify(`Maximum ${maxFiles} images allowed. You can add ${maxFiles - images.length} more.`, 'error');
            return;
        }

        const validFiles = newFiles.filter(file => {
            const isValidType = file.type.startsWith('image/');
            const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit

            if (!isValidType) {
                toastify(`${file.name} is not a valid image file`, 'error');
                return false;
            }
            if (!isValidSize) {
                toastify(`${file.name} is too large (max 5MB)`, 'error');
                return false;
            }
            return true;
        });

        setImages(prevImages => [...prevImages, ...validFiles]);

        // Create previews for new files and add to existing previews
        const newPreviews = validFiles.map(file => ({
            file,
            url: URL.createObjectURL(file),
            name: file.name
        }));

        setImagePreviews(prevPreviews => [...prevPreviews, ...newPreviews]);

        // Clear the input so the same files can be selected again if needed
        e.target.value = '';
    };

    const clearAllImages = () => {
        // Clean up all object URLs
        imagePreviews.forEach(preview => {
            URL.revokeObjectURL(preview.url);
        });
        setImages([]);
        setImagePreviews([]);
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        // Clean up object URL
        URL.revokeObjectURL(imagePreviews[index].url);
        setImages(newImages);
        setImagePreviews(newPreviews);
    };

    const handleFinish = async () => {
        try {
            if (!payerType) {
                toastify("Please select a payer type.", "error");
                return;
            }

            if ((payerType === "tenant" || payerType === "landlord") && !payer) {
                toastify(`Please select a specific ${payerType}.`, "error");
                return;
            }

            const facilityId = await getItem('selectedFacilityId');
            const propertyManagerPhoneNumber = await getCurrentUser();
            const finalPayer = payerType === 'propertyManager' ? '' : payer;

            const formData = new FormData();
            formData.append('needsFix', needsFix);
            formData.append('reviewText', reviewText);
            formData.append('payer', payer);
            formData.append('payerType', payerType);
            formData.append('requester', requester);
            formData.append('description', description);
            formData.append('services', JSON.stringify(services));
            formData.append('totalAmount', totalCost);

            if (propertyManagerPhoneNumber) {
                formData.append('propertyManagerPhoneNumber', propertyManagerPhoneNumber);
            }

            if (images.length > 0) {
                images.forEach((image, index) => {
                    formData.append('images', image);
                });
            }

            if (costAttachment) {
                formData.append('costAttachment', costAttachment);
            } else {
                console.log('No cost attachment selected');
            }

            const response = await makeRequest2(`${finishReviewURL}/${ticketId}/${facilityId}`, 'POST', formData);

            if (response.success) {
                toastify('Ticket Review Finished Successfully', 'success');
                handleClear();
                navigate('/facility/ticket_management/tickets');
            } else {
                console.error(response.error);
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    }

    const handleClear = () => {
        setNeedsFix('');
        setReviewText('');
        setPayer('');
        imagePreviews.forEach(preview => {
            URL.revokeObjectURL(preview.url);
        });
        // Reset states
        setImages([]);
        setImagePreviews([]);
    }

    const handlePayerTypeChange = async (selectedPayerType) => {
        if (currentTicket?.customerId && selectedPayerType === 'tenant') {
            setPayer(currentTicket.customerId);
        }
        else if (currentTicket?.customerId && selectedPayerType === 'landlord') {
            const customerId = currentTicket.customerId;
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getTicketLandlordURL}/${facilityId}/${customerId}`, 'GET');

            if (response.success && response.data.landlord) {
                setPayerDetails(response.data.landlord);
                setPayer(response.data.landlord._id);
            } else {
                setPayer('');
                toastify(response.message || 'Landlord not found', 'error');
            }
        }
        else {
            // For staff-raised tickets or property manager, clear the payer
            setPayer('');
        }
    };

    // if payerType is already set
    useEffect(() => {
        if (payerType && currentTicket) {
            handlePayerTypeChange(payerType);
        }
    }, [payerType, currentTicket, allCustomers]);

    useEffect(() => {
        if (ticketId) {
            getCompleteTicketData();
        }
    }, [ticketId]);

    useEffect(() => {
        getCurrentUser();
        fetchCustomersForFacility();
    }, []);

    return (
        <Layout>
            <div className="card">
                <div className="card-header">
                    <Link to={"#"} onClick={() => navigate(-1)}>
                        <span>
                            <i className="ti ti-arrow-narrow-left"></i> Back
                        </span>
                    </Link>
                </div>
            </div>
            <div className="wizard-container">
                {/* Step Navigation */}
                <div className="card">
                    <div className="card-body p-3">
                        <ul className="nav nav-pills nav-justified">
                            {steps.map((step, index) => (
                                <li className="nav-item" key={step}>
                                    <button
                                        className={`nav-link ${currentStep === step ? 'active' : ''} ${completedSteps.includes(step) ? '' : 'disabled'
                                            }`}
                                        onClick={() =>
                                            completedSteps.includes(step) ? setCurrentStep(step) : null
                                        }
                                        disabled={!completedSteps.includes(step)}
                                    >
                                        {step}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Step Content */}
                <div className="card mt-3">
                    <div className="card-body">
                        {currentStep === 'StartReview' && (
                            <div>
                                <h2>Start Review</h2>
                                <div className="mb-4">
                                    <label htmlFor="needsFix" className="form-label">
                                        Needs to be fixed: <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <select
                                        id="needsFix"
                                        className="form-select"
                                        value={needsFix}
                                        onChange={(e) => setNeedsFix(e.target.value)}
                                    >
                                        <option value="">Select</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>

                                {needsFix === 'no' && (
                                    <div className="text-end mt-4">
                                        <textarea
                                            className="form-control mb-3"
                                            rows="3"
                                            placeholder="Enter reason for closing the ticket"
                                            value={closeReason}
                                            onChange={(e) => setCloseReason(e.target.value)}
                                        ></textarea>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => setConfirmVisible(true)}
                                            disabled={isClosing}
                                        >
                                            {isClosing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Closing...
                                                </>
                                            ) : (
                                                'Close Ticket'
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* {needsFix === 'yes' && (
                                    
                                )} */}
                            </div>
                        )}

                        {currentStep === 'UploadReview' && (
                            <div>
                                <h4>Upload Review</h4>
                                <div className="mb-4">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-camera"></i> Upload Photo(s) (Optional)
                                        {images.length > 0 && (
                                            <span className="badge bg-primary ms-2">
                                                {images.length}/5
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    <div className="form-text">
                                        Attach up to 5 photos for better understanding of the issue. Max 5MB per image.
                                        {images.length > 0 && (
                                            <span className="text-success">
                                                {` Currently selected: ${images.length} image(s)`}
                                            </span>
                                        )}
                                    </div>
                                    {/* Image Previews */}
                                    {imagePreviews.length > 0 && (
                                        <div className="mt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <small className="text-muted">Selected Images:</small>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={clearAllImages}
                                                >
                                                    Clear All
                                                </button>
                                            </div>
                                            <div className="row g-2">
                                                {imagePreviews.map((preview, index) => (
                                                    <div key={index} className="col-md-3">
                                                        <div className="position-relative">
                                                            <img
                                                                src={preview.url}
                                                                alt={`Preview ${index + 1}`}
                                                                className="img-thumbnail w-100"
                                                                style={{ height: '100px', objectFit: 'cover' }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                                style={{ transform: 'translate(25%, -25%)' }}
                                                                onClick={() => removeImage(index)}
                                                            >
                                                                <i className="ti ti-x"></i>
                                                            </button>
                                                            <div className="small text-muted mt-1 text-truncate">
                                                                {preview.name}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="reviewText" className="form-label">
                                        Write a Review: <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <textarea
                                        id="reviewText"
                                        className="form-control"
                                        rows="4"
                                        value={reviewText}
                                        onChange={(e) => setReviewText(e.target.value)}
                                    ></textarea>
                                </div>
                            </div>
                        )}

                        {currentStep === 'EstimatedCosts' && (
                            <div>
                                <h4>Estimated Costs</h4>
                                {services.map((service, index) => (
                                    <div className="row align-items-center mb-3" key={index}>
                                        <div className="col-4">
                                            <label className="form-label">Service Description <span style={{ color: 'red' }}>*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Service Description"
                                                value={services[index]?.description || ''}
                                                onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-3">
                                            <label className="form-label">Quantity <span style={{ color: 'red' }}>*</span></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Qty"
                                                min="1"
                                                value={services[index]?.quantity || 1}
                                                onChange={(e) => handleServiceChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                            />
                                        </div>
                                        <div className="col-3">
                                            <label className="form-label">Unit Price <span style={{ color: 'red' }}>*</span></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Unit Price"
                                                step="0.01"
                                                value={services[index]?.unitPrice || ''}
                                                onChange={(e) => handleServiceChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="col-1">
                                            <label className="form-label">Amount <span style={{ color: 'red' }}>*</span></label>
                                            <div className="form-control-plaintext">
                                                {(services[index]?.quantity || 1) * (services[index]?.unitPrice || 0).toFixed(2)}
                                            </div>
                                        </div>
                                        {/* Allow removal for dynamically added fields only */}
                                        {index > 0 && (
                                            <div className="col-1 text-end">
                                                <button
                                                    className="btn btn-danger mt-4"
                                                    onClick={() => removeServiceField(index)}
                                                >
                                                    -
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {/* Add Button */}
                                <div className="text-end">
                                    <button
                                        className="btn btn-primary"
                                        onClick={addServiceField}
                                    >
                                        + Add Another Service
                                    </button>
                                </div>
                                {/* Total Cost Display */}
                                <div className="mt-3">
                                    <h5>Total Cost: {totalCost.toFixed(2)}</h5>
                                </div>
                                {/* File Upload */}
                                <div className="mt-3">
                                    <label className="form-label">Attach Detailed Cost Breakdown (if any)</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) => setCostAttachement(e.target.files[0])}
                                    />
                                </div>
                            </div>
                        )}

                        {currentStep === 'PayerApproval' && (
                            <div>
                                <h4>Payer Approval</h4>
                                <div className="mb-3">
                                    <label htmlFor="payer-type-dropdown" className="form-label">
                                        Who will pay: <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <select
                                        id="payer-type-dropdown"
                                        className="form-select"
                                        value={payerType}
                                        onChange={(e) => {
                                            setPayerType(e.target.value);
                                            setPayerSearchTerm('');
                                            handlePayerTypeChange(e.target.value);
                                        }}
                                    >
                                        <option value="" disabled>
                                            Select payer type
                                        </option>
                                        <option value="landlord">Landlord</option>
                                        <option value="propertyManager">Property Manager</option>
                                        <option value="tenant">Tenant</option>
                                    </select>
                                </div>
                                {/* Show automatic selection for tenant-raised tickets */}
                                {payerType === 'tenant' && currentTicket?.customerId && (
                                    <div className="mb-3">
                                        <div className="alert alert-info d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>Auto-selected:</strong> Ticket was raised by tenant - automatically selecting them as payer.
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => {
                                                    setPayer('');
                                                    setPayerSearchTerm('');
                                                }}
                                            >
                                                Change Selection
                                            </button>
                                        </div>
                                        {payer === currentTicket?.customerId && (() => {
                                            const tenantCustomer = allCustomers.find(c => c._id === currentTicket?.customerId);
                                            return tenantCustomer ? (
                                                <div className="p-2 bg-light rounded">
                                                    <div className="fw-bold">
                                                        {tenantCustomer.firstName} {tenantCustomer.lastName}
                                                    </div>
                                                    <small className="text-muted">
                                                        {tenantCustomer.phoneNumber} • {tenantCustomer.email}
                                                    </small>
                                                </div>
                                            ) : (
                                                <div className="text-muted">Tenant information not found</div>
                                            );
                                        })()}
                                        {/* Show selected tenant when manually changed */}
                                        {payer && payer !== currentTicket?.customerId && (() => {
                                            const selectedTenant = allCustomers.find(c => c._id === payer);
                                            return selectedTenant ? (
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small className="text-muted">Selected tenant:</small>
                                                    <div className="fw-bold">
                                                        {selectedTenant.firstName} {selectedTenant.lastName}
                                                    </div>
                                                    <small className="text-muted">
                                                        {selectedTenant.phoneNumber} • {selectedTenant.email}
                                                    </small>
                                                    {selectedTenant.unitNumber && (
                                                        <small className="d-block text-muted">
                                                            Unit: {selectedTenant.unitNumber}
                                                        </small>
                                                    )}
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                )}
                                {/* Show automatic selection for landlord when tenant raised ticket */}
                                {payerType === 'landlord' && currentTicket?.customerId && (
                                    <div className="mb-3">
                                        <div className="alert alert-info d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>Auto-selected:</strong> Landlord for the tenant's unit has been found.
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => {
                                                    setPayer('');
                                                    setPayerDetails(null);
                                                    setPayerSearchTerm('');
                                                }}
                                            >
                                                Change Selection
                                            </button>
                                        </div>
                                        {payerDetails && payer === payerDetails._id && (
                                            <div className="p-2 bg-light rounded">
                                                <div className="fw-bold">
                                                    {payerDetails.firstName} {payerDetails.lastName}
                                                </div>
                                                <small className="text-muted">
                                                    {payerDetails.phoneNumber} • {payerDetails.email}
                                                </small>
                                            </div>
                                        )}
                                        {/* Show selected landlord when manually changed */}
                                        {payer && (!payerDetails || payer !== payerDetails._id) && (() => {
                                            const selectedLandlord = allCustomers.find(c => c._id === payer);
                                            return selectedLandlord ? (
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small className="text-muted">Selected landlord:</small>
                                                    <div className="fw-bold">
                                                        {selectedLandlord.firstName} {selectedLandlord.lastName}
                                                    </div>
                                                    <small className="text-muted">
                                                        {selectedLandlord.phoneNumber} • {selectedLandlord.email}
                                                    </small>
                                                    {selectedLandlord.unitNumber && (
                                                        <small className="d-block text-muted">
                                                            Unit: {selectedLandlord.unitNumber}
                                                        </small>
                                                    )}
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                )}
                                {/* Show search interface for staff-raised tickets, when auto-selection fails, or when user clicks "Change Selection" */}
                                {((payerType === 'landlord' || payerType === 'tenant') &&
                                    ((!currentTicket?.customerId || currentTicket?.userId) ||
                                        (currentTicket?.customerId && !payer))) && (
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Select specific {payerType}:
                                            </label>
                                            {currentTicket?.userId && (
                                                <div className="alert alert-warning mb-2">
                                                    <small>Ticket was raised by staff - please search and select the {payerType} manually.</small>
                                                </div>
                                            )}
                                            {/* Search input */}
                                            <div className="mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={`Search ${payerType}s by name, phone, or email...`}
                                                    value={payerSearchTerm}
                                                    onChange={(e) => setPayerSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            {/* Filtered results */}
                                            <div className="border rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {(() => {
                                                    const filteredCustomers = allCustomers
                                                        .filter(customer => {
                                                            // Filter by customer type first
                                                            const isCorrectType = payerType === 'landlord'
                                                                ? (customer.customerType === 'home owner' && customer.residentType === 'non-resident')
                                                                : customer.customerType === 'tenant';

                                                            if (!isCorrectType) return false;

                                                            // Then filter by search term
                                                            if (!payerSearchTerm) return true;

                                                            const searchLower = payerSearchTerm.toLowerCase();
                                                            const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
                                                            const phone = customer.phoneNumber?.toLowerCase() || '';
                                                            const email = customer.email?.toLowerCase() || '';

                                                            return fullName.includes(searchLower) ||
                                                                phone.includes(searchLower) ||
                                                                email.includes(searchLower);
                                                        });

                                                    if (filteredCustomers.length === 0) {
                                                        return (
                                                            <div className="p-3 text-muted text-center">
                                                                {payerSearchTerm ? `No ${payerType}s found matching "${payerSearchTerm}"` : `No ${payerType}s available`}
                                                            </div>
                                                        );
                                                    }
                                                    return filteredCustomers.map((customer) => (
                                                        <div
                                                            key={customer._id}
                                                            className={`p-2 border-bottom cursor-pointer hover-bg-light ${payer === customer._id ? 'bg-primary text-white' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPayer(customer._id);
                                                                // If selecting a landlord manually, also set the payer details
                                                                if (payerType === 'landlord') {
                                                                    setPayerDetails(customer);
                                                                }
                                                            }}
                                                        >
                                                            <div className="fw-bold">
                                                                {customer.firstName} {customer.lastName}
                                                            </div>
                                                            <small className="text-muted">
                                                                {customer.phoneNumber} • {customer.email}
                                                            </small>
                                                            {customer.unitNumber && (
                                                                <small className="d-block text-muted">
                                                                    Unit: {customer.unitNumber}
                                                                </small>
                                                            )}
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                            {/* Selected payer display for manual selection */}
                                            {payer && (
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small className="text-muted">Selected {payerType}:</small>
                                                    {(() => {
                                                        const selectedCustomer = allCustomers.find(c => c._id === payer);
                                                        return selectedCustomer ? (
                                                            <div>
                                                                <div className="fw-bold">
                                                                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {selectedCustomer.phoneNumber} • {selectedCustomer.email}
                                                                </small>
                                                                {selectedCustomer.unitNumber && (
                                                                    <small className="d-block text-muted">
                                                                        Unit: {selectedCustomer.unitNumber}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="text-muted">Customer information not found</div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                {/* Property Manager doesn't need specific selection since there's typically one per property */}
                                {payerType === 'propertyManager' && (
                                    <div className="mb-3">
                                        <p className="text-muted">
                                            Property Manager will be charged for this ticket.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Navigation Buttons */}
                        {needsFix === 'yes' && (
                            <div className="d-flex justify-content-between mt-3">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleBackToPrevious}
                                    disabled={steps.indexOf(currentStep) === 0}
                                >
                                    Back
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        if (steps.indexOf(currentStep) === steps.length - 1) {
                                            handleFinish();
                                        } else {
                                            handleNextStep();
                                        }
                                    }}
                                >
                                    {steps.indexOf(currentStep) === steps.length - 1 ? 'Finish' : 'Next'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                visible={confirmVisible}
                onHide={hideConfirmDialog}
                message="Are you sure you want to close this ticket?"
                onConfirm={handleCloseTicket}
                isLoading={isClosing} // Pass loading state to dialog
                confirmText={isClosing ? "Closing..." : "Yes"} // Dynamic text
            />
        </Layout>
    );
};

export default StartReview;

